// Tag management ON the lift record (replaces the standalone /office/nfc page). A
// self-contained per-row action on the Lifts register, in the same idiom as UploadCertButton /
// CertificatesButton. It shows the tag status, the tag URL (with copy + a scan-to-test
// QR), and the office actions: generate a URL for an untagged lift, send the URL to an engineer
// (email or WhatsApp) to programme the physical sticker, and -- admin only -- rotate or remove
// the tag. "RFID" is the customer-facing term; the internal route/token is "NFC" (NTAG213).
//
// SESSION AMZ: THE SAME CONTROL NOW SERVES BOTH OF THE LIFT'S TAGS. The customer-facing RFID
// sticker in the car, and the Guv POP Tag (Session AMZ as "Tapping For Tracking"), the INTERNAL tag sited with the paper
// log card in the motor room. The office does the identical job with both (generate, send to an
// engineer, rotate, remove), so this is one component switched by `tagKind` rather than two files
// that would drift; everything that actually differs between the two lives in TAG_OPS below, in
// one place, so a card wired to the wrong endpoint is one visibly wrong line rather than a subtle
// fork. The tap variant must never reach a customer surface: it is office-only, as this file is.
import { useEffect, useRef, useState } from "react";
import { api } from "../../../lib/api";
import type { Asset } from "../../../lib/resources";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import type { EngineerListItem } from "../../../lib/api";
import { useAuth } from "../../../lib/auth";
import { copyText } from "../../../lib/clipboard";
import { useSingleTimeout } from "../../../lib/useSingleTimeout";
import { QrImage } from "../../../components/QrImage";
import {
  rfidTagStatus,
  RFID_STATUS_META,
  TAP_STATUS_META,
  rfidUrl,
  tapUrl,
  rfidSendMessage,
  tapSendMessage,
  whatsAppNumber,
  whatsAppHref,
  mailtoHref,
} from "../../../lib/rfid";
import type { RfidAssetKind, RfidStatusMeta, RfidTagStatus, TagKind } from "../../../lib/rfid";
import { ConfirmDeleteModal } from "../../../components/ConfirmDeleteModal";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../../components/ModalShell";
import { Button } from "../../../components/Button";
import { StatusPill } from "../../../components/StatusPill";
import { statusEntry } from "../../../lib/statusRegistry";

// The lift shape these controls need. `attendanceTapToken` / `attendanceTapSentAt` now live on
// `Asset` itself (src/lib/resources.ts, Session AMZ), beside `nfcToken` / `rfidSentAt`, which is
// their honest home: the office asset routes have always spread the whole row, so both were already
// on the wire and only the interface had not caught up. The alias is kept as the name these controls
// read by, so a future third tag has one place to widen rather than every prop in this file.
export type TaggableAsset = Asset;

function eleRefOf(a: Asset, kind: RfidAssetKind): string {
  return a.eleNumber ?? a.legacyEleNumber ?? (kind === "equipment" ? "Equipment" : "Lift");
}

// Everything that differs between the lift's two tags, in ONE table. Adding a case here is how a
// future tag arrives; forking the component is not. Note that `read`, `generate` and `regenerate`
// all normalise to the same { token, sentAt } pair, so the component below never sees either
// tag's column names and cannot write one tag's response into the other's state.
interface TagOps {
  // The modal heading and the aria label. Also the noun used in the remove confirmation.
  title: string;
  // The control on the record itself. Distinct per tag because BOTH cards are on the lift detail
  // page at once, and two buttons reading "Manage tag" would be ambiguous to a screen reader and to
  // anyone scanning the page.
  manageLabel: string;
  urlLabel: string;
  statusMeta: Record<RfidTagStatus, RfidStatusMeta>;
  read: (a: TaggableAsset) => { token: string | null; sentAt: string | null };
  url: (origin: string, token: string, kind: RfidAssetKind) => string;
  message: (o: { assetRef: string; siteName: string; url: string; kind: RfidAssetKind }) => string;
  emailSubject: (eleRef: string) => string;
  generate: (id: string) => Promise<{ token: string | null; sentAt: string | null }>;
  regenerate: (id: string) => Promise<{ token: string | null; sentAt: string | null }>;
  markSent: (id: string) => Promise<{ sentAt: string | null }>;
  remove: (id: string) => Promise<unknown>;
  regenerateTitle: (eleRef: string) => string;
  regenerateMessage: string;
  removeTitle: (eleRef: string) => string;
  removeMessage: (noun: string) => string;
  // Whether the office may open the URL from here (Preview link + scan-to-test QR). TRUE for the
  // customer RFID page, which is a read. FALSE for the attendance tap, because that page RECORDS a
  // tap on open: an office click, or a camera pointed at a QR on a desk, would manufacture an
  // attendance record against a lift nobody visited. The office has no reason to open it anyway,
  // only to copy it or send it, so the affordance is removed rather than captioned with a warning.
  openable: boolean;
}

const TAG_OPS: Record<TagKind, TagOps> = {
  rfid: {
    title: "RFID tag",
    manageLabel: "Manage tag",
    urlLabel: "RFID URL",
    statusMeta: RFID_STATUS_META,
    read: (a) => ({ token: a.nfcToken ?? null, sentAt: a.rfidSentAt ?? null }),
    url: (origin, token, kind) => rfidUrl(origin, token, kind),
    message: (o) => rfidSendMessage(o),
    emailSubject: (eleRef) => `RFID tag for ${eleRef}`,
    generate: async (id) => {
      const r = await api.assets.generateNfc(id);
      return { token: r.nfcToken, sentAt: r.rfidSentAt };
    },
    regenerate: async (id) => {
      const r = await api.assets.regenerateNfc(id);
      return { token: r.nfcToken, sentAt: r.rfidSentAt };
    },
    markSent: async (id) => ({ sentAt: (await api.assets.markNfcSent(id)).rfidSentAt }),
    remove: (id) => api.assets.removeNfc(id),
    regenerateTitle: (eleRef) => `Regenerate the RFID URL for ${eleRef}?`,
    regenerateMessage:
      "This creates a new URL and invalidates the existing RFID sticker. The engineer will need to reprogram it on site.",
    removeTitle: (eleRef) => `Remove the RFID tag for ${eleRef}?`,
    removeMessage: (noun) =>
      `This clears the URL and the sent status for this ${noun}. Any physical sticker stops working immediately. You can generate a new URL later.`,
    openable: true,
  },
  tap: {
    title: "Guv POP Tag",
    manageLabel: "Manage POP Tag",
    urlLabel: "POP Tag URL",
    statusMeta: TAP_STATUS_META,
    read: (a) => ({ token: a.attendanceTapToken ?? null, sentAt: a.attendanceTapSentAt ?? null }),
    // No equipment variant: the attendance tag has one page shape (see tapUrl's own note), so the
    // asset kind is deliberately ignored here rather than silently producing a /tap/e/ path that
    // no route serves.
    url: (origin, token) => tapUrl(origin, token),
    message: (o) => tapSendMessage(o),
    emailSubject: (eleRef) => `Guv POP Tag for ${eleRef}`,
    generate: async (id) => {
      const r = await api.assets.generateTap(id);
      return { token: r.attendanceTapToken, sentAt: r.attendanceTapSentAt };
    },
    regenerate: async (id) => {
      const r = await api.assets.regenerateTap(id);
      return { token: r.attendanceTapToken, sentAt: r.attendanceTapSentAt };
    },
    markSent: async (id) => ({ sentAt: (await api.assets.markTapSent(id)).attendanceTapSentAt }),
    remove: (id) => api.assets.removeTap(id),
    regenerateTitle: (eleRef) => `Regenerate the POP Tag URL for ${eleRef}?`,
    regenerateMessage:
      "This creates a new URL and stops the existing motor room tag working. The engineer will need to reprogram it on site. The taps already recorded are kept.",
    removeTitle: (eleRef) => `Remove the POP Tag for ${eleRef}?`,
    // Says plainly what a remove does NOT do. The history is the point of this tag, and an office
    // user reaching for Remove needs to know it survives before they press it, not afterwards.
    removeMessage: (noun) =>
      `This clears the URL and the sent status for this ${noun}. Any motor room POP Tag stops working immediately. The taps already recorded are kept, and you can generate a new URL later.`,
    openable: false,
  },
};

// Session ACT: the same button serves lifts AND care equipment. `kind` (default "lift") decides the
// public URL shape (/nfc vs /nfc/e), the send-to-engineer message (no auto-dialler for equipment)
// and the plain-English noun in the copy. A lift caller is unchanged.
//
// `onOpenChange` (Session AEA, optional): reports the tag modal opening/closing to a host that
// stacks this control inside its OWN modal (the dashboard's Send RFID tag picker guards its
// Escape on it, so one Escape closes one level, never the whole stack -- each ModalShell has
// its own window keydown listener and an unguarded outer close would unmount every child).
export function RfidTagButton({
  asset,
  kind = "lift",
  tagKind = "rfid",
  onOpenChange,
}: {
  asset: TaggableAsset;
  kind?: RfidAssetKind;
  // WHICH of the lift's two tags this control manages (Session AMZ). Deliberately NOT called
  // `kind`: that name is already taken above by RfidAssetKind ("lift" | "equipment", what the tag
  // is stuck to), and the two are orthogonal -- the register's equipment rows pass both. Defaults
  // to "rfid" so every pre-AMZ call site is unchanged.
  tagKind?: TagKind;
  onOpenChange?: (open: boolean) => void;
}) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // D57 (Session ARA): rotating a tag's URL is operational, not destructive, so it opens to office
  // alongside admin. Removing a tag stays admin-only (isAdmin alone, below) -- that one destroys the
  // sticker's binding outright.
  const canRegenerate = isAdmin || user?.role === "office";
  const noun = kind === "equipment" ? "equipment" : "lift";
  const ops = TAG_OPS[tagKind];

  // Local copies so the badge + modal reflect each action without a full table reload.
  const initial = ops.read(asset);
  const [token, setToken] = useState<string | null>(initial.token);
  const [sentAt, setSentAt] = useState<string | null>(initial.sentAt);

  const [open, setOpen] = useState(false);
  // Every open/close goes through openTag/closeTag so the host is always told; the ref pair
  // lets the unmount cleanup report a still-open modal as closed (a host must never be left
  // guarding against a modal that no longer exists).
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  const reportedOpenRef = useRef(false);
  const notifyOpen = (o: boolean) => {
    reportedOpenRef.current = o;
    onOpenChangeRef.current?.(o);
  };
  useEffect(() => {
    return () => {
      if (reportedOpenRef.current) onOpenChangeRef.current?.(false);
    };
  }, []);
  const openTag = () => {
    setOpen(true);
    notifyOpen(true);
  };
  const closeTag = () => {
    setOpen(false);
    notifyOpen(false);
  };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const resetCopiedLater = useSingleTimeout();
  const [showQr, setShowQr] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);

  const status = rfidTagStatus(token, sentAt);
  const meta = ops.statusMeta[status];
  const url = token ? ops.url(window.location.origin, token, kind) : "";
  const eleRef = eleRefOf(asset, kind);
  const siteName = asset.site?.name ?? "Unknown site";

  async function generate() {
    setBusy(true);
    setError(null);
    try {
      const res = await ops.generate(asset.id);
      setToken(res.token);
      setSentAt(res.sentAt);
    } catch (err) {
      setError(friendlyApiError(err, "Could not generate the URL. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function regenerate() {
    setBusy(true);
    setError(null);
    try {
      const res = await ops.regenerate(asset.id);
      setToken(res.token);
      setSentAt(res.sentAt);
      setRegenOpen(false);
      setShowQr(false);
    } catch (err) {
      setError(friendlyApiError(err, "Could not regenerate the URL. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await ops.remove(asset.id);
      setToken(null);
      setSentAt(null);
      setRemoveOpen(false);
      setShowQr(false);
    } catch (err) {
      setError(friendlyApiError(err, "Could not remove the tag. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!url) return;
    const ok = await copyText(url);
    if (ok) {
      setCopied(true);
      resetCopiedLater(() => setCopied(false), 2000);
    } else {
      setError("Could not copy automatically. Select the URL above to copy it.");
    }
  }

  return (
    <div className="space-y-1">
      <RfidStatusLine status={status} meta={meta} />
      <button
        type="button"
        onClick={() => {
          setError(null);
          openTag();
        }}
        className="block rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-green"
      >
        {ops.manageLabel}
      </button>

      {open && (
        <ModalShell
          ariaLabel={ops.title}
          onClose={() => !busy && !sendOpen && !regenOpen && !removeOpen && closeTag()}
          width="max-w-md"
          panelClassName="max-h-[90vh] overflow-y-auto p-6 text-left"
        >
            <div className="mb-1 flex items-start justify-between gap-3">
              <h2 className="font-heading text-xl text-ele-strong">{ops.title}</h2>
              <button onClick={closeTag} aria-label="Close" className="text-ele-body hover:text-ele-strong">
                ✕
              </button>
            </div>
            <p className="mb-4 text-sm text-ele-body">
              {eleRef}
              {asset.site?.name ? `, ${asset.site.name}` : ""}
            </p>

            <div className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
              <RfidStatusLine status={status} meta={meta} />
              <p className="mt-1 text-xs text-ele-body">{meta.detail}</p>
            </div>

            {token ? (
              <div className="mb-4">
                <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-blue" htmlFor={`rfid-url-${asset.id}`}>
                  {ops.urlLabel}
                </label>
                <input
                  id={`rfid-url-${asset.id}`}
                  readOnly
                  value={url}
                  onFocus={(e) => e.currentTarget.select()}
                  className="w-full rounded-ele border border-ele-purple-accent bg-ele-field-inset px-3 py-2 text-xs text-ele-body outline-none"
                />
                <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                  <button type="button" onClick={copy} className="text-ele-green hover:underline">
                    {copied ? "Copied" : "Copy URL"}
                  </button>
                  {ops.openable && (
                    <>
                      <a href={url} target="_blank" rel="noreferrer" className="text-ele-blue hover:underline">
                        Preview
                      </a>
                      <button type="button" onClick={() => setShowQr((q) => !q)} className="text-ele-green hover:underline">
                        {showQr ? "Hide QR" : "QR code"}
                      </button>
                    </>
                  )}
                </div>
                {ops.openable && showQr && (
                  <div className="mt-3 flex flex-col items-center gap-2">
                    <div className="rounded-ele bg-white p-3">
                      <QrImage url={url} />
                    </div>
                    <p className="text-center text-xs text-ele-body opacity-70">Scan with a phone camera to open the live RFID page.</p>
                  </div>
                )}
                {/* The attendance tap page RECORDS on open, so there is nothing safe to preview or
                    scan from a desk. Said out loud rather than left as a missing control, so nobody
                    reads it as a bug and adds one back. */}
                {!ops.openable && (
                  <p className="mt-2 text-xs text-ele-body opacity-70">
                    Opening this URL records a tap against this lift, so there is no preview or QR code here. Copy the
                    URL to programme the tag.
                  </p>
                )}
              </div>
            ) : null}

            {error && (
              <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
                {error}
              </p>
            )}

            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              {!token ? (
                <Button size="sm" className={MODAL_ACTION_BTN} onClick={generate} disabled={busy}>
                  {busy ? "Generating..." : "Generate URL"}
                </Button>
              ) : (
                <>
                  <Button
                    size="sm"
                    className={MODAL_ACTION_BTN}
                    onClick={() => {
                      setError(null);
                      setSendOpen(true);
                    }}
                  >
                    Send to engineer
                  </Button>
                  {canRegenerate && (
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        setRegenOpen(true);
                      }}
                      className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-green ${MODAL_ACTION_BTN}`}
                    >
                      Regenerate URL
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        setRemoveOpen(true);
                      }}
                      className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-danger ${MODAL_ACTION_BTN}`}
                    >
                      Remove tag
                    </button>
                  )}
                </>
              )}
            </div>

          {sendOpen && token && (
            <SendToEngineerModal
              assetId={asset.id}
              eleRef={eleRef}
              siteName={siteName}
              url={url}
              kind={kind}
              ops={ops}
              onSent={(iso) => setSentAt(iso)}
              onClose={() => setSendOpen(false)}
            />
          )}

          {regenOpen && (
            <ConfirmDialog
              title={ops.regenerateTitle(eleRef)}
              message={ops.regenerateMessage}
              confirmLabel="Regenerate"
              busy={busy}
              busyLabel="Regenerating..."
              onConfirm={regenerate}
              onCancel={() => setRegenOpen(false)}
            />
          )}

          <ConfirmDeleteModal
            open={removeOpen}
            title={ops.removeTitle(eleRef)}
            message={ops.removeMessage(noun)}
            confirmLabel="Remove tag"
            variant="destructive"
            singleStep
            busy={busy}
            error={null}
            onConfirm={remove}
            onCancel={() => setRemoveOpen(false)}
          />
        </ModalShell>
      )}
    </div>
  );
}

// The status meta is passed in rather than looked up here: the badge renders for either tag, and a
// second lookup inside it is a second place the wrong wording could appear.
function RfidStatusLine({ status, meta }: { status: RfidTagStatus; meta: RfidStatusMeta }) {
  return (
    <span data-status={status}>
      <StatusPill word={meta.label} tone={statusEntry("rfid", status).tone} />
    </span>
  );
}

// The "send to engineer" modal: pick the engineer, edit the message, and send the URL by email
// or WhatsApp. Sending records rfidSentAt (so the status flips to "Sent to engineer"). A
// missing engineer email or mobile shows a warning instead of a broken link.
function SendToEngineerModal({
  assetId,
  eleRef,
  siteName,
  url,
  kind,
  ops,
  onSent,
  onClose,
}: {
  assetId: string;
  eleRef: string;
  siteName: string;
  url: string;
  kind: RfidAssetKind;
  // Which tag is being sent. The message body and the "record the send" endpoint both come from
  // here, so a send can never stamp one tag's sent timestamp while quoting the other's URL.
  ops: TagOps;
  onSent: (iso: string | null) => void;
  onClose: () => void;
}) {
  const [engineers, setEngineers] = useState<EngineerListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string>("");
  const [message, setMessage] = useState(ops.message({ assetRef: eleRef, siteName, url, kind }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.engineers
      .list()
      .then((list) => {
        if (!live) return;
        const active = list.filter((e) => e.isActive);
        setEngineers(active);
        setSelectedId(active[0]?.id ?? "");
      })
      .catch(() => {
        if (live) setError("Could not load the engineers. Please try again.");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);

  const engineer = engineers.find((e) => e.id === selectedId) ?? null;
  const waNumber = whatsAppNumber(engineer?.mobile);
  const subject = ops.emailSubject(eleRef);

  // Record the send first; only open the channel if that succeeds, so the status reflects
  // reality. The mailto / wa.me link then opens in the user's mail client / a new tab.
  async function send(channel: "email" | "whatsapp") {
    if (!engineer) return;
    setBusy(true);
    setError(null);
    try {
      const res = await ops.markSent(assetId);
      onSent(res.sentAt);
      if (channel === "email" && engineer.email) {
        window.location.href = mailtoHref(engineer.email, subject, message);
      } else if (channel === "whatsapp" && waNumber) {
        window.open(whatsAppHref(waNumber, message), "_blank", "noopener,noreferrer");
      }
      onClose();
    } catch {
      setError("Could not record the send. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Send to engineer" onClose={() => !busy && onClose()} scrim="bg-black/70" width="max-w-md" panelClassName="max-h-[90vh] overflow-y-auto p-6 text-left">
        <div className="mb-3 flex items-start justify-between gap-3">
          <h2 className="font-heading text-lg text-ele-strong">Send to engineer</h2>
          <button onClick={onClose} aria-label="Close" className="text-ele-body hover:text-ele-strong">
            ✕
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-ele-body">Loading engineers...</p>
        ) : engineers.length === 0 ? (
          <p className="rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-body">
            No active engineers are set up. Add one under People, then Engineers, with their email and mobile.
          </p>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm text-ele-body" htmlFor="rfid-send-engineer">
                Engineer
              </label>
              <select
                id="rfid-send-engineer"
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
              >
                {engineers.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.displayName}
                    {e.techId ? ` (${e.techId})` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm text-ele-body" htmlFor="rfid-send-message">
                Message
              </label>
              <textarea
                id="rfid-send-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
              />
            </div>

            {error && (
              <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
                {error}
              </p>
            )}

            <div className="space-y-2">
              {engineer?.email ? (
                <Button size="sm" className="w-full" onClick={() => send("email")} disabled={busy}>
                  {busy ? "Sending..." : `Send via email (${engineer.email})`}
                </Button>
              ) : (
                <p className="rounded-ele border border-amber-500 bg-ele-surface px-3 py-2 text-xs text-amber-400">
                  No email on file for this engineer. Add one under People, then Engineers, to send by email.
                </p>
              )}

              {waNumber ? (
                <Button variant="secondary" size="sm" className="w-full" onClick={() => send("whatsapp")} disabled={busy}>
                  {busy ? "Sending..." : "Send via WhatsApp"}
                </Button>
              ) : (
                <p className="rounded-ele border border-amber-500 bg-ele-surface px-3 py-2 text-xs text-amber-400">
                  No mobile number on file for this engineer. Add one under People, then Engineers, to send by WhatsApp.
                </p>
              )}
            </div>
          </div>
        )}
    </ModalShell>
  );
}

// A small bespoke confirm (the shared ConfirmDeleteModal is for deletes; a regenerate is not a
// delete and needs its own busy label).
function ConfirmDialog({
  title,
  message,
  confirmLabel,
  busy,
  busyLabel,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  busy: boolean;
  busyLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <ModalShell ariaLabel={title} onClose={() => !busy && onCancel()} scrim="bg-black/70" width="max-w-md" panelClassName="p-5">
        <h2 className="font-heading text-lg text-ele-strong">{title}</h2>
        <p className="mt-2 text-sm text-ele-body">{message}</p>
        <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm disabled:opacity-50 ${MODAL_ACTION_BTN}`}
          >
            Cancel
          </button>
          <Button variant="danger" className={MODAL_ACTION_BTN} onClick={onConfirm} disabled={busy}>
            {busy ? busyLabel : confirmLabel}
          </Button>
        </div>
    </ModalShell>
  );
}

